import { useCallback, useContext, useEffect, useState } from "react";

import EcheckinDao from "../../dao/EcheckinDao";
import StudentInfoDao from "../../dao/StudentInfoDao";

import { DASH_LINKS } from "../../const/AppCardLinks";
import { ECHECKIN_EXCLUDED_SCHOOLS } from "../../const/EcheckinConst";
import { formatDate, getEpoch, getTodayWithTime } from "../DateFormatter";
import { getAttributeIds } from "../../const/StudentInfoConst";
import { GlobalContext } from "../../components/contextProvider/ContextProvider";

/**
 * ELECTRONIC CHECK-IN BUSINESS RULES
 * ------------------------------------------------------------------------------------------------
 * Student must have a StudentNumber
 * get schoolYearDto (active schoolYear via user service)
 * get locationDto via studentNumber
 * get checkinWindow BySchoolYearAndType(PARENT_CHECKIN)
 * if checkinWindow and locationKey NOT IN (296, 297, 311,383) - removed 394 from list
 * Express Check-In, Online Student Check-In

 * Status: In Progress, Not Started, Completed - No guardian affirm for checkin = Not Started, custom attribute for checkin  = complete
 * No More Info button

 * main button - begin checkin or manage additional options or continue checkin
 * ------------------------------------------------------------------------------------------------
 * Custom hook to determine if a student's guardian has access to the ECheck-In app tile
 * @name useEcheckinApp
 * @param {{}} student
 * @returns {{}} {appObj, loading}
 */
export const useEcheckinApp = (student) => {
    const { dispatch, state } = useContext(GlobalContext);
    const { allLocationDtos, schoolYearDto, studentAppObjects, token, username } = state || {};

    const [appObj, setAppObj] = useState(null);
    const [firstRun, setFirstRun] = useState(true);
    const [loading, setLoading] = useState(true);
    const [locationDto, setLocationDto] = useState(null);

    const studentKey = student?.key || null;
    const existing = studentAppObjects?.[studentKey]?.ECHECKIN || null;

    /**
     * @name dispatchAppObject
     * @description Dispatch the appObj to the context
     * @param {{}} obj
     */
    const dispatchAppObject = useCallback(
        (obj) => {
            dispatch({
                type: "StudentAppObjects",
                studentAppObjects: {
                    ...studentAppObjects,
                    [studentKey]: {
                        ...studentAppObjects?.[studentKey],
                        ECHECKIN: {
                            appObj: obj
                        }
                    }
                }
            });
        },
        [dispatch, studentAppObjects, studentKey]
    );

    /**
     * If we have the student's apps in context, set the appObj for return
     */
    useEffect(() => {
        if (existing) {
            setAppObj(existing.appObj);
            setLoading(false);
        }
    }, [existing]);

    /**
     * Get the selected student's school location
     */
    useEffect(() => {
        if (existing) {
            return;
        }
        if (!allLocationDtos || !student) {
            setAppObj(null);

            return;
        }

        /**
         * @name getLocationDto
         * @description Retrieve the selected student's school location
         * @returns {{} | null}
         */
        const getLocationDto = () => {
            try {
                const { locationKey, schoolId } = student;
                let locMatch = [];
                if (schoolId) {
                    locMatch = allLocationDtos.reduce((result, obj) => {
                        const { icId } = obj;
                        const schoolMatch = icId.filter((item) => parseInt(item, 10) === parseInt(schoolId, 10));
                        if (schoolMatch && schoolMatch.length > 0) {
                            result.push(obj);
                        }

                        return result;
                    }, []);
                } else if (locationKey) {
                    locMatch = allLocationDtos.filter((obj) => parseInt(obj.key, 10) === parseInt(locationKey, 10));
                }
                if (locMatch && locMatch?.length > 0) {
                    if (!ECHECKIN_EXCLUDED_SCHOOLS.includes(parseInt(locMatch[0].key, 10))) {
                        setLocationDto(locMatch[0]);
                    }
                }
            } catch (error) {
                console.error(`Unable to retrieve a Location for Student ${student.studentNumber}:\n`, error);
            }
        };

        getLocationDto();
    }, [allLocationDtos, existing, student]);

    /**
     * If the location is valid, construct the appObj
     */
    useEffect(() => {
        if (existing) {
            return;
        }
        if (!locationDto || !schoolYearDto || !token || !student?.studentNumber) {
            setAppObj(null);
            setLoading(false);

            return;
        }

        setLoading(true);

        /**
         * @name fetchCustomAttribute
         * @description Retrieve the student's custom attributes
         * @async
         * @param {{}} app
         * @param {{}} map
         * @returns {{}} updatedAppObj
         */
        const fetchCustomAttribute = async (app, map) => {
            try {
                const attributes = getAttributeIds(map.primSecFlag);
                const attributeIds = Object.keys(attributes).join();
                const options = {
                    action: "customAttributesRead",
                    params: {
                        attributeIds,
                        checkForActiveEnrollment: true
                    },
                    retrieveAllFlag: true,
                    studentNumber: student.studentNumber,
                    token
                };
                const response = await StudentInfoDao(options);
                if (response && response.data) {
                    const { payload } = response.data;
                    if (payload) {
                        const ca = map.primSecFlag === "PRIMARY" ? "check_online" : "check_online_sec";
                        const checkinMatch = payload.find(
                            (attribute) => attribute.element === ca && attribute.objectName === "Enrollment"
                        );
                        if (checkinMatch && parseInt(checkinMatch.value, 10) === 1) {
                            const updatedAppObj = {
                                ...app,
                                // Completed
                                status: "Global_Completed",
                                statusColor: "#4a8319", // green
                                subStatus: "Global_Completed",
                                subStatusRange: formatDate(checkinMatch?.createdDate, true, true) || null
                            };

                            return updatedAppObj;
                        }
                    }
                }

                return app;
            } catch (error) {
                console.error(`Unable to retrieve Custom Attributes for Student ${student.studentNumber}:\n`, error);
            }
        };

        /**
         * @name fetchEcheckin
         * @async
         * @returns {{} | null}
         */
        const fetchEcheckin = async () => {
            try {
                const options = {
                    action: "checkinWindowParentCheckin",
                    locKey: locationDto.key,
                    params: {
                        feederLocationId: locationDto.feederLocationId
                    },
                    schoolYearKey: schoolYearDto.key,
                    token
                };
                const response = await EcheckinDao(options);
                if (response && response.data) {
                    const { payload } = response.data;
                    const { checkinEndDate, checkinMessage, checkinStartDate, postCheckinMessage, preCheckinMessage } =
                        payload;
                    let message = checkinMessage;
                    const todayEpoch = getEpoch(getTodayWithTime());
                    const isOpen = todayEpoch >= getEpoch(checkinStartDate) && todayEpoch < getEpoch(checkinEndDate);
                    if (todayEpoch < getEpoch(checkinStartDate)) {
                        message = preCheckinMessage;
                    } else if (todayEpoch > getEpoch(checkinEndDate)) {
                        message = postCheckinMessage;
                    }
                    const echeckinObj = {
                        app: "ECHECKIN",
                        body: "ECApp_Body",
                        /* eslint-disable */
                        buttons: isOpen
                            ? [
                                {
                                    // Go to Express Check-In
                                    label: "ECApp_Btn1",
                                    href: student ? `${DASH_LINKS.echeckin}/${student.studentNumber}` : null
                                }
                            ]
                            : null,
                        /* eslint-enable */
                        endDate: formatDate(payload.checkinEndDate, true, true),
                        icon: "EXPRESS_CHECKIN_ICON",
                        moreInfo: null,
                        startDate: formatDate(payload.checkinStartDate, true, true),
                        // Not Started
                        status: "Global_NotStarted",
                        statusColor: "#707070", // grey3a
                        subBody: message,
                        // Express Check-In
                        title: "ECApp_Header"
                    };
                    return echeckinObj;
                }

                return null;
            } catch (error) {
                console.error(
                    `Unable to retrieve Parent Checkin Window for School Year ${schoolYearDto.key}:\n`,
                    error
                );
            }
        };

        /**
         * @name fetchGuardianMap
         * @async
         * @returns {{} | null}
         */
        const fetchGuardianMap = async () => {
            try {
                const options = {
                    action: "guardianStudentMapByStudentNumberRead",
                    params: {
                        locKey: locationDto.key
                    },
                    studentNumber: student.studentNumber,
                    token,
                    username
                };
                const response = await EcheckinDao(options);
                if (response && response.data) {
                    const { payload } = response.data;
                    if (payload) {
                        return payload;
                    }
                }

                return null;
            } catch (error) {
                console.error(`Unable to retrieve Guardian Student Map for Student ${student.studentNumber}:\n`, error);
            }
        };

        /**
         * @name fetchSignature
         * @async
         * @param {{}} app
         * @param {{}} map
         * @returns {{}} updatedAppObj
         */
        const fetchSignature = async (app, map) => {
            try {
                // has the echeckin process begun?
                // look for guardian confirm
                const options = {
                    action: "eSignatureRead",
                    mapId: map.key,
                    type: "GUARDIAN_AFFIRM",
                    token
                };
                const response = await EcheckinDao(options);
                if (response && response.data) {
                    const { payload } = response.data;
                    if (payload) {
                        const updatedAppObj = {
                            ...app,
                            // In Progress
                            body: "ECApp_Body",
                            status: "Global_InProgress",
                            statusColor: "#0669B3" // pearl-blue
                        };

                        return updatedAppObj;
                    }
                }

                return app;
            } catch (error) {
                console.error(`Unable to retrieve Guardian Affirmation for Student ${student.studentNumber}:\n`, error);
            }
        };

        /**
         * @name run
         * @description Retrieve the student's ECheck-In App
         * @returns {null}
         */
        const run = async () => {
            try {
                const app = await fetchEcheckin();
                if (app) {
                    const map = await fetchGuardianMap();
                    if (map) {
                        const newAppObj = await fetchSignature(app, map);
                        const finalAppObj = await fetchCustomAttribute(newAppObj, map);
                        dispatchAppObject(finalAppObj);

                        return;
                    }
                    dispatchAppObject(app);

                    return;
                }
                dispatchAppObject(null);

                return;
            } catch (error) {
                console.error(`Unable to retrieve Express Check-In App ${student.studentNumber}:\n`, error);
            } finally {
                setLoading(false);
                setFirstRun(true);
            }
        };

        if (firstRun) {
            setFirstRun(false);
            run();
        }
    }, [dispatchAppObject, existing, firstRun, locationDto, schoolYearDto, student, token, username]);

    return { appObj, loading };
};
